import { createSlice } from '@reduxjs/toolkit';
import socket from '../services/socketClient';

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: {
    psaNotifications: [],
    lostNotifications: [],
    foundNotifications: [],
    petServiceNotifications: [],
    newPostsAvailable: 0,
    feedRefreshTrigger: null,
  },
  reducers: {
    // Add new PSA notification and mark as unread
    addPsaNotification: (state, action) => {
      state.psaNotifications.unshift({ ...action.payload, read: false });
    },
    // Add new Lost notification and mark as unread
    addLostNotification: (state, action) => {
      state.lostNotifications.unshift({ ...action.payload, read: false });
    },
    // Add new Found notification and mark as unread
    addFoundNotification: (state, action) => {
      state.foundNotifications.unshift({ ...action.payload, read: false });
    },
    // Add new Pet Service notification and mark as unread
    addPetServiceNotification: (state, action) => {
      state.petServiceNotifications.unshift({ ...action.payload, read: false });
    },
    // Remove a PSA notification by ID
    removePsaNotification: (state, action) => {
      state.psaNotifications = state.psaNotifications.filter(
        (notification) => notification._id !== action.payload
      );
    },
    // Remove a Lost notification by ID
    removeLostNotification: (state, action) => {
      state.lostNotifications = state.lostNotifications.filter(
        (notification) => notification._id !== action.payload
      );
    },
    // Remove a Found notification by ID
    removeFoundNotification: (state, action) => {
      state.foundNotifications = state.foundNotifications.filter(
        (notification) => notification._id !== action.payload
      );
    },
    // Remove a Pet Service notification by ID
    removePetServiceNotification: (state, action) => {
      state.petServiceNotifications = state.petServiceNotifications.filter(
        (notification) => notification._id !== action.payload
      );
    },
    // Update a PSA notification by ID
    updatePsaNotification: (state, action) => {
      const { id, updatedData } = action.payload;
      state.psaNotifications = state.psaNotifications.map(notification =>
        notification._id === id ? { ...notification, ...updatedData } : notification
      );
    },
    // Update a Lost notification by ID
    updateLostNotification: (state, action) => {
      const { id, updatedData } = action.payload;
      state.lostNotifications = state.lostNotifications.map(notification => 
        notification._id === id ? { ...notification, ...updatedData } : notification
      );
    },
    // Update a Found notification by ID
    updateFoundNotification: (state, action) => {
      const { id, updatedData } = action.payload;
      state.foundNotifications = state.foundNotifications.map(notification =>
        notification._id === id ? { ...notification, ...updatedData } : notification
      );
    },
    // Update a Pet Service notification by ID
    updatePetServiceNotification: (state, action) => {
      const { id, updatedData } = action.payload;
      state.petServiceNotifications = state.petServiceNotifications.map(notification =>
        notification._id === id ? { ...notification, ...updatedData } : notification
      );
    },
    // Mark all PSA notifications as read
    markPsaNotificationsAsRead: (state) => {
      state.psaNotifications.forEach((notification) => {
        notification.read = true;
      });
    },
    // Mark all Lost notifications as read
    markLostNotificationsAsRead: (state) => {
      state.lostNotifications.forEach((notification) => {
        notification.read = true;
      });
    },
    // Mark all Found notifications as read
    markFoundNotificationsAsRead: (state) => {
      state.foundNotifications.forEach((notification) => {
        notification.read = true;
      });
    },
    // Mark all Pet Service notifications as read
    markPetServiceNotificationsAsRead: (state) => {
      state.petServiceNotifications.forEach((notification) => {
        notification.read = true;
      });
    },
    // Increment new posts available counter
    incrementNewPostsAvailable: (state) => {
      state.newPostsAvailable += 1;
    },
    // Clear new posts available counter
    clearNewPostsAvailable: (state) => {
      state.newPostsAvailable = 0;
    },
    // Trigger a feed refresh
    triggerFeedRefresh: (state) => {
      state.feedRefreshTrigger = Date.now();
    },
  },
});

// Export actions for dispatching notifications
export const { 
  addPsaNotification, 
  addLostNotification, 
  addFoundNotification,
  addPetServiceNotification,
  removePsaNotification,
  removeLostNotification,
  removeFoundNotification,
  removePetServiceNotification,
  updatePsaNotification,
  updateLostNotification,
  updateFoundNotification,
  updatePetServiceNotification,
  markPsaNotificationsAsRead,
  markLostNotificationsAsRead,
  markFoundNotificationsAsRead,
  markPetServiceNotificationsAsRead,
  incrementNewPostsAvailable,
  clearNewPostsAvailable,
  triggerFeedRefresh,
} = notificationsSlice.actions;

// Selectors to get the count of individual unread notification types
export const selectPsaNotificationCount = (state) =>
  state.notifications.psaNotifications.filter(notification => !notification.read).length;

export const selectLostNotificationCount = (state) =>
  state.notifications.lostNotifications.filter(notification => !notification.read).length;

export const selectFoundNotificationCount = (state) =>
  state.notifications.foundNotifications.filter(notification => !notification.read).length;

export const selectPetServiceNotificationCount = (state) =>
  state.notifications.petServiceNotifications.filter(notification => !notification.read).length;

// Thunk to handle real-time updates
export const initializeSocketListeners = () => (dispatch, getState) => {
  socket.on('newPSA', (newPSA) => {
    dispatch(addPsaNotification(newPSA));
    const currentUserId = getState().auth.user?._id;
    if (newPSA.userId !== currentUserId) {
      dispatch(incrementNewPostsAvailable());
    }
  });

  socket.on('newLostAnimal', (newLostAnimal) => {
    dispatch(addLostNotification(newLostAnimal));
    const currentUserId = getState().auth.user?._id;
    if (newLostAnimal.userId !== currentUserId) {
      dispatch(incrementNewPostsAvailable());
    }
  });

  socket.on('newFoundAnimal', (newFoundAnimal) => {
    dispatch(addFoundNotification(newFoundAnimal));
    const currentUserId = getState().auth.user?._id;
    if (newFoundAnimal.userId !== currentUserId) {
      dispatch(incrementNewPostsAvailable());
    }
  });

  socket.on('newPetService', (newPetService) => {
    dispatch(addPetServiceNotification(newPetService));
    const currentUserId = getState().auth.user?._id;
    if (newPetService.userId !== currentUserId) {
      dispatch(incrementNewPostsAvailable());
    }
  });

  // Cleanup function to remove listeners when not needed
  return () => {
    socket.off('newPSA');
    socket.off('newLostAnimal');
    socket.off('newFoundAnimal');
    socket.off('newPetService');
  };
};

export const selectNewPostsAvailable = (state) => state.notifications.newPostsAvailable;

export const selectFeedRefreshTrigger = (state) => state.notifications.feedRefreshTrigger;

export default notificationsSlice.reducer;
